import React from 'react';
import {Route} from 'react-router-dom';
import routeMapping from './routeMapping.json';
import PalStyleWrapperPage from '../pages/PalStyleWrapper';
import LandingPageStyleWrapperPage from '../pages/LandingPage/StyleWrapper';
import MedicalNecessityChecklist from '../pages/MedicalNecessityChecklist';
import PrivateRoute from '../components/PrivateRoute';

export const NotFoundPage = () => <h3>Not Found</h3>;

export const COMPONENT_MAPPING = {
  PAL: PalStyleWrapperPage,
  LANDINGPAGE: LandingPageStyleWrapperPage,
  MEDICALNECESSITYCHECKLIST: MedicalNecessityChecklist
};

/**
 * Checks whether a property exists and is truthy within the settings object by evaluating a dot-separated path
 *
 * @param {Object} settings - The object to search within.
 * @param {string} path - The dot-separated string representing the path to the desired property.
 * @returns {boolean} - `true` if the property exists and is truthy, otherwise `false`.
 */
const evaluatePropertyPath = (settings, path) => {
  const keys = path.split('.');
  let currentValue = settings;

  for (const key of keys) {
    if (currentValue && currentValue.hasOwnProperty(key)) {
      currentValue = currentValue[key];
    } else {
      return false;
    }
  }

  return Boolean(currentValue);
}

/**
   * Evaluates a logical condition composed of nested property paths within the settings.
   *
   * @param {string} condition - A string containing one or more dot-separated property paths joined by logical operators.
   * @param {Object} settings - The object containing the settings to evaluate.
   * @returns {boolean} - `true` if all conditions are met, otherwise `false`.
   */
const evaluateCondition = (condition, settings) => {
  try {
    const conditionParts = condition.split(' && ').map((part) => part.trim());

    return conditionParts.every((part) => {
      if (part.startsWith('siteType')) {
        // Custom logic to evaluate siteType conditions
        const [key, value] = part.split('===').map((str) => str.trim().replace(/['"]/g, ''));
        return settings[key] && settings[key].toLowerCase() === value.toLowerCase();
      }

      if (part.startsWith('!')) {
        const negatedPart = part.slice(1);
        return !evaluatePropertyPath(settings, negatedPart);
      }

      return evaluatePropertyPath(settings, part);
    });
  } catch (error) {
    return false;
  }
};

const renderRoutes = (settings) => (
  routeMapping.routes.map((route) => {

    const shouldRenderComponent = route['criteriaToRenderPath'] ? evaluateCondition(route['criteriaToRenderPath'], settings) : true;
    const authenticate = route['checkAuthenticationCondition'] ? settings.authenticate : false;

    if (!shouldRenderComponent) {
      return null;
    }

    let wrapperComponent = NotFoundPage;

    // Prioritize rendering the wrapperComponentToRender setting over the siteType.
    if (route['wrapperComponentToRender'] && COMPONENT_MAPPING[route['wrapperComponentToRender'].toUpperCase()]) {
      wrapperComponent = COMPONENT_MAPPING[route['wrapperComponentToRender'].toUpperCase()];
    } else if (settings.siteType) {
      wrapperComponent = COMPONENT_MAPPING[settings.siteType.toUpperCase()]
    }

    if (authenticate) {
      return (
        <PrivateRoute
          key={route.path}
          exact={route.exact}
          path={route.path}
          component={wrapperComponent}
        />
      );
    } else if (shouldRenderComponent) {
      return (
        <Route
          key={route.path}
          exact={route.exact}
          path={route.path}
          component={wrapperComponent}
        />
      );
    }
    return null;
  })
);

export {evaluatePropertyPath, evaluateCondition, renderRoutes}